import { getToken, isNative } from "./native";
import { LANG, t } from "@shared/i18n";

/** En web : même origine (chaîne vide). En natif : URL absolue de l'API de production. */
export const API_BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";

/** Événements globaux : l'interface ouvre la bonne fenêtre (formules, accord IA). */
export const UPGRADE_EVENT = "gbc:upgrade";
export const AI_CONSENT_EVENT = "gbc:ai-consent";
function broadcast(code: string, details: unknown) {
  const d = details as { reason?: string } | undefined;
  if (code === "upgrade_required") window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail: details }));
  else if (d?.reason === "ai_consent") window.dispatchEvent(new CustomEvent(AI_CONSENT_EVENT));
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

const WS_KEY = "gbc.workspace";
let workspaceId: string | null = null;
try {
  workspaceId = localStorage.getItem(WS_KEY);
} catch {
  workspaceId = null;
}
export const getWorkspaceId = () => workspaceId;
/** Espace de travail actif (personnel si null), transmis au serveur qui vérifie l'appartenance. */
export function setWorkspaceId(id: string | null) {
  workspaceId = id;
  try {
    if (id) localStorage.setItem(WS_KEY, id);
    else localStorage.removeItem(WS_KEY);
  } catch {
    /* stockage indisponible : l'espace reste actif pour cette session */
  }
}

export function authHeaders(): Record<string, string> {
  const token = isNative ? getToken() : null;
  return { "X-Lang": LANG, ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(workspaceId ? { "X-Workspace-Id": workspaceId } : {}) };
}

export async function api<T = unknown>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      ...rest,
      headers: { ...(json !== undefined ? { "Content-Type": "application/json" } : {}), ...authHeaders(), ...headers },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
    });
  } catch {
    throw new ApiError(0, "network", t("Connexion impossible. Vérifiez votre réseau."));
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = body?.error ?? {};
    // Accès retiré à l'espace mémorisé : retour à l'espace personnel.
    if (res.status === 403 && err.details?.reason === "workspace" && workspaceId) {
      setWorkspaceId(null);
      window.location.assign("/app");
    }
    broadcast(err.code, err.details);
    throw new ApiError(res.status, err.code ?? "error", err.message ?? t("Une erreur est survenue."), err.details);
  }
  return body as T;
}

type StreamHandlers = {
  onEvent: (event: string, data: unknown) => void;
  signal?: AbortSignal;
};

/** POST + lecture d'un flux SSE (EventSource ne permet pas le POST). */
export async function apiStream(path: string, json: unknown, { onEvent, signal }: StreamHandlers) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    signal,
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...authHeaders() },
    body: JSON.stringify(json),
  });
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => null);
    broadcast(body?.error?.code, body?.error?.details);
    throw new ApiError(res.status, body?.error?.code ?? "error", body?.error?.message ?? t("La requête a échoué."), body?.error?.details);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = "message";
      let data = "";
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (data) onEvent(event, JSON.parse(data));
    }
  }
}
